/**
 * Algolia Autocomplete — Desktop full-width search panel
 * Uses @algolia/autocomplete-js with a custom render function for
 * a two-column layout: suggestions/categories left, product grid right.
 *
 * The render function uses params.render (Preact's render, passed by the
 * library) and params.elements (pre-rendered source VNodes keyed by sourceId).
 * See: https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-js/autocomplete/#render
 *
 * Desktop only (≥750px) — mobile search handled separately.
 * Integrates with SlAlgoliaEvents for Insights click tracking.
 *
 * VAT note: the autocomplete panel re-renders on every keystroke, so
 * vatMode()/fmtPrice()/vatLabel() read fresh localStorage state each render.
 * data-price-inc attributes are included for external VAT toggle scripts.
 */
(function() {
  'use strict';

  /* ── Helpers (shared with algolia-search.liquid) ── */

  function vatMode() {
    try { return localStorage.getItem('vatPreference') || 'inc'; } catch(e) { return 'inc'; }
  }

  function fmtPrice(p) {
    if (p == null) return '';
    var n = parseFloat(p);
    if (isNaN(n)) return '';
    return '\u00A3' + (vatMode() === 'ex' ? n / 1.2 : n).toFixed(2);
  }

  function vatLabel() { return vatMode() === 'ex' ? 'EX VAT' : 'INC VAT'; }

  /* Cheapest single-unit variant price for the customer */
  function getCheapestPrice(hit) {
    if (window.__b2bTier && hit.meta && hit.meta.custom && hit.meta.custom.b2b_prices) {
      var b2bPrices = hit.meta.custom.b2b_prices;
      var cheapest = Infinity;
      for (var vid in b2bPrices) {
        if (b2bPrices.hasOwnProperty(vid) && b2bPrices[vid][window.__b2bTier] !== undefined) {
          var vp = b2bPrices[vid][window.__b2bTier];
          if (vp < cheapest) cheapest = vp;
        }
      }
      return (cheapest < Infinity) ? cheapest : (hit.variants_min_price || hit.price);
    }
    return hit.variants_min_price || hit.price;
  }

  /* Star rating — returns array of 'full', 'half', 'empty' for 5 stars */
  function starTypes(rating) {
    var rounded = Math.round(rating * 2) / 2;
    var stars = [];
    for (var i = 1; i <= 5; i++) {
      if (rounded >= i) stars.push('full');
      else if (rounded >= i - 0.5) stars.push('half');
      else stars.push('empty');
    }
    return stars;
  }

  /* ── Wait for CDN dependencies ── */

  function waitForDeps(cb) {
    var attempts = 0;
    (function check() {
      if (window['@algolia/autocomplete-js'] &&
          typeof algoliasearch !== 'undefined' &&
          window.SlAlgoliaConfig) {
        return cb();
      }
      if (++attempts > 200) {
        console.warn('[Algolia Autocomplete] Dependencies not loaded after 10s — aborting');
        return;
      }
      setTimeout(check, 50);
    })();
  }

  /* ── Backdrop management ── */

  var backdrop = null;

  function showBackdrop() {
    if (backdrop) { backdrop.style.display = 'block'; backdrop.style.pointerEvents = ''; return; }
    backdrop = document.createElement('div');
    backdrop.className = 'sl-aa-backdrop';
    backdrop.setAttribute('role', 'presentation');
    backdrop.addEventListener('click', function() {
      hideBackdrop();
    });
    document.body.appendChild(backdrop);
  }

  function hideBackdrop() {
    if (backdrop) {
      backdrop.style.display = 'none';
      backdrop.style.pointerEvents = 'none';
    }
  }

  /* ── Initialise autocomplete on inline header search ── */

  function init() {
    /* Desktop only (>=1024px) — mobile + tablet get a separate search experience */
    if (window.innerWidth < 1024) return;

    var config = window.SlAlgoliaConfig;
    var autocomplete = window['@algolia/autocomplete-js'].autocomplete;
    var searchClient = algoliasearch(config.appId, config.apiKey);
    var suggestionsIndex = config.suggestionsIndex || 'shopify_products_query_suggestions';
    var productsIndex = config.indexName;

    /* Find desktop inline search forms — skip mobile (--mobile class) */
    var forms = document.querySelectorAll('.header-search-inline:not(.header-search-inline--mobile)');
    if (!forms.length) return;

    /* Track the last queryID for Insights */
    var lastQueryID = '';
    /* Track total product results for "View all X results" link */
    var lastProductsTotal = 0;

    /* Screen reader live region for result announcements */
    var srAnnounce = document.createElement('div');
    srAnnounce.setAttribute('role', 'status');
    srAnnounce.setAttribute('aria-live', 'polite');
    srAnnounce.setAttribute('aria-atomic', 'true');
    srAnnounce.className = 'visually-hidden';
    document.body.appendChild(srAnnounce);

    forms.forEach(function(form) {
      /* Create autocomplete container */
      var container = document.createElement('div');
      container.className = 'sl-aa-wrap';
      form.parentNode.insertBefore(container, form);

      /* Hide original form — autocomplete replaces it visually */
      form.style.display = 'none';
      form.dataset.algoliaReplaced = 'true';

      /* Survive Shopify section morph: when morph diffs the header section
         against server HTML, it removes sl-aa-wrap (not in server response).
         The detached node still holds its Preact tree — just re-insert it. */
      var formParent = form.parentNode;
      new MutationObserver(function() {
        if (!document.contains(container)) {
          var target = formParent.querySelector('.header-search-inline:not(.header-search-inline--mobile)');
          if (target) {
            formParent.insertBefore(container, target);
            target.style.display = 'none';
            target.dataset.algoliaReplaced = 'true';
          }
        }
      }).observe(formParent, { childList: true });

      autocomplete({
        container: container,
        placeholder: 'Search for products...',
        openOnFocus: false,
        detachedMediaQuery: 'none',
        panelPlacement: 'full-width',

        getSources: function(params) {
          var query = params.query;
          if (!query) return [];

          var sources = [];

          /* Source 1 — Query Suggestions */
          sources.push({
            sourceId: 'suggestions',
            getItems: function() {
              return searchClient
                .search([{
                  indexName: suggestionsIndex,
                  query: query,
                  params: { hitsPerPage: 5 }
                }])
                .then(function(res) { return res.results[0].hits; });
            },
            getItemUrl: function(params) {
              return '/search?q=' + encodeURIComponent(params.item.query) + '&type=product';
            },
            templates: {
              header: function(params) {
                return params.html`<div class="sl-aa-section-heading">Suggestions</div>`;
              },
              item: function(params) {
                var item = params.item;
                return params.html`<a class="sl-aa-suggestion-link" href="${'/search?q=' + encodeURIComponent(item.query) + '&type=product'}">
                  <svg class="sl-aa-icon-search" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <span class="sl-aa-suggestion-text">${item.query}</span>
                </a>`;
              },
              noResults: function() { return ''; }
            }
          });

          /* Source 2 — Products (3+ characters) */
          if (query.length >= 3) {
            sources.push({
              sourceId: 'products',
              getItems: function() {
                return searchClient
                  .search([{
                    indexName: productsIndex,
                    query: query,
                    params: {
                      /* Hex configurator components/parent carry tags:do-not-display
                         (spec 2.2) so they never surface in autocomplete suggestions. */
                      filters: 'NOT tags:"do-not-display"',
                      hitsPerPage: 6,
                      clickAnalytics: true,
                      enablePersonalization: true,
                      userToken: config.userToken
                    }
                  }])
                  .then(function(res) {
                    lastQueryID = res.results[0].queryID || '';
                    lastProductsTotal = res.results[0].nbHits || 0;
                    return res.results[0].hits;
                  });
              },
              getItemUrl: function(params) {
                return '/products/' + (params.item.handle || '');
              },
              onSelect: function(params) {
                var item = params.item;
                try {
                  if (window.SlAlgoliaEvents && item.__queryID) {
                    window.SlAlgoliaEvents.trackSearchClick(
                      String(item.objectID),
                      item.__queryID,
                      item.__position
                    );
                    sessionStorage.setItem('sl_algolia_query_id', item.__queryID);
                  }
                } catch(e) { /* silent */ }
              },
              templates: {
                item: function(params) {
                  var item = params.item;
                  var img = item.product_image || item.image || '';
                  /* 120×120 thumbnail — request 120px-wide image from Shopify CDN (preserves any existing ?v= cache-buster). */
                  var imgSized = img ? img + (img.indexOf('?') !== -1 ? '&' : '?') + 'width=120' : '';
                  var price = getCheapestPrice(item);
                  var priceInPence = Math.round(parseFloat(price) * 100) || 0;
                  var rating = (item.meta && item.meta.custom && item.meta.custom.reviews_average)
                    ? parseFloat(item.meta.custom.reviews_average) : 0;

                  /* Build SKU for Reviews.io (same format as collection page) */
                  var parentSku = (item.meta && item.meta.custom && item.meta.custom.parent_sku) ? item.meta.custom.parent_sku : '';
                  var allVariantSkus = (item.meta && item.meta.custom && item.meta.custom.all_variant_skus) ? item.meta.custom.all_variant_skus : (item.sku || '');
                  var sku = parentSku ? parentSku + ';' + allVariantSkus : allVariantSkus;

                  return params.html`<a class="cpg-card sl-aa-product-card" href="${'/products/' + (item.handle || '')}">
                    <div class="sl-aa-product-img-wrap">
                      ${img
                        ? params.html`<img class="sl-aa-product-img" src="${imgSized}" alt="${item.title || ''}" width="120" height="120" loading="lazy" />`
                        : params.html`<div class="sl-aa-product-img sl-aa-product-img--placeholder"></div>`}
                    </div>
                    <div class="sl-aa-product-info">
                      <span class="sl-aa-product-title">${item.title || ''}</span>
                      ${sku
                        ? params.html`<div key="rating-${item.objectID}" class="ruk_rating_snippet" data-sku="${sku}" style="min-height:16px;"></div>`
                        : ''}
                      ${rating > 0
                        ? params.html`<div class="sl-aa-product-stars">${starTypes(rating).map(function(type) {
                            if (type === 'full') return params.html`<span class="sl-aa-star sl-aa-star--full">★</span>`;
                            if (type === 'half') return params.html`<span class="sl-aa-star sl-aa-star--half">★</span>`;
                            return params.html`<span class="sl-aa-star sl-aa-star--empty">☆</span>`;
                          })}</div>`
                        : ''}
                      <span class="sl-aa-product-meta">
                        <span key="price-${item.objectID}" class="sl-aa-product-price" data-price-inc="${priceInPence}">${fmtPrice(price)}</span>
                        <span key="vat-${item.objectID}" class="sl-aa-product-vat">${vatLabel()}</span>
                      </span>
                    </div>
                  </a>`;
                },
                noResults: function(params) {
                  return params.html`<div class="sl-aa-no-results">
                    No products found for "${params.state.query}"
                  </div>`;
                }
              }
            });
          }

          return sources;
        },

        /* ── Custom two-column render ──
           params.render is Preact's render function (passed by the library).
           params.elements is an object keyed by sourceId → pre-rendered VNode.
           params.state.collections has raw hit data for category extraction.
           See: https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-js/autocomplete/#render */
        render: function(params, root) {
          var render = params.render;
          var html = params.html;
          var elements = params.elements;
          var state = params.state;

          var suggestionsEl = elements.suggestions || '';
          var productsEl = elements.products || '';

          /* Extract unique product_type values for category links */
          var categories = [];
          var seenTypes = {};
          var collections = state.collections || [];
          for (var ci = 0; ci < collections.length; ci++) {
            if (collections[ci].source &&
                collections[ci].source.sourceId === 'products') {
              var productItems = collections[ci].items || [];
              for (var j = 0; j < productItems.length; j++) {
                var pt = productItems[j].product_type;
                if (pt && !seenTypes[pt] && categories.length < 6) {
                  seenTypes[pt] = true;
                  categories.push(pt);
                }
              }
              break;
            }
          }

          var query = state.query || '';
          var searchUrl = '/search?q=' + encodeURIComponent(query) + '&type=product';

          render(
            html`<div class="aa-PanelLayout sl-aa-panel">
              <div class="sl-aa-col-left">
                ${suggestionsEl}
                ${categories.length > 0
                  ? html`<nav class="sl-aa-categories" aria-label="Related categories">
                      <div class="sl-aa-section-heading">Categories</div>
                      <ul class="sl-aa-categories-list">
                        ${categories.map(function(cat) {
                          return html`<li>
                            <a class="sl-aa-category-link" href="${'/collections/all?q=' + encodeURIComponent(cat)}">
                              ${cat}
                            </a>
                          </li>`;
                        })}
                      </ul>
                    </nav>`
                  : ''}
              </div>
              <div class="sl-aa-col-right">
                <div class="sl-aa-products-header">
                  <span class="sl-aa-section-heading">Products</span>
                  ${productsEl && lastProductsTotal > 0
                    ? html`<a class="sl-aa-products-viewall" href="${searchUrl}"
                        aria-label="${'View all ' + lastProductsTotal + ' results for ' + query}">
                        View all ${lastProductsTotal} results →
                      </a>`
                    : ''}
                </div>
                ${productsEl
                  ? productsEl
                  : html`<p class="sl-aa-type-more">Type 3+ characters to see products</p>`}
              </div>
            </div>`,
            root
          );

          /* Trigger Reviews.io rating snippets for newly rendered cards */
          if (typeof ratingSnippet === 'function') {
            try {
              ratingSnippet('ruk_rating_snippet', {
                store: 'simple-lighting',
                color: '#F5A523',
                linebreak: false,
                text: 'Reviews',
                showEmptyStars: true
              });
            } catch(e) { /* Reviews.io not loaded — OK */ }

            /* Hide "(0 Reviews)" snippets — they waste space in compact cards */
            setTimeout(function() {
              document.querySelectorAll('.aa-Panel .ruk_rating_snippet').forEach(function(el) {
                var text = el.textContent || '';
                if (text.indexOf('(0 Reviews)') !== -1 || text.indexOf('(0)') !== -1 || text.trim() === '') {
                  el.style.display = 'none';
                }
              });
            }, 600);
          }

          /* Announce results to screen readers */
          if (lastProductsTotal > 0 && query) {
            srAnnounce.textContent = lastProductsTotal + ' products found for ' + query;
          }
        },

        onStateChange: function(params) {
          if (params.state.isOpen) {
            showBackdrop();
          } else {
            hideBackdrop();
          }
        },

        onSubmit: function(params) {
          if (params.state.query) {
            window.location.href = '/search?q=' + encodeURIComponent(params.state.query) + '&type=product';
          }
        },

        navigator: {
          navigate: function(params) {
            window.location.assign(params.itemUrl);
          }
        }
      });
    });
  }

  /* ── Boot ── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { waitForDeps(init); });
  } else {
    waitForDeps(init);
  }
})();










